import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PillBadge from './PillBadge';
import BookCallModal from './BookCallModal';

const ContactSection = ({ userData }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <section id="contact" className="py-12 sm:py-20 relative overflow-hidden" style={{ backgroundImage: 'url(/BG.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      {/* Faded Logo Collage Background */}
      <div 
        className="absolute inset-0 opacity-10"
       
      />
      
      {/* Abstract Background */}
      <div className="absolute inset-0 bg-black/40">
        <motion.div
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
         
        />
        
        <motion.div
          animate={{ 
            rotate: [360, 0],
            y: [0, -20, 0]
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-24 right-24 w-32 h-32 opacity-15"
          style={{
            background: `
              radial-gradient(circle at 40% 30%, rgba(255,255,255,0.1) 0%, transparent 70%),
              linear-gradient(45deg, rgba(75, 85, 99, 0.2) 0%, rgba(55, 65, 81, 0.2) 100%)
            `,
            borderRadius: '50%',
            filter: 'blur(1px)'
          }}
        />
        
        <motion.div
          animate={{ 
            rotateZ: [0, 360],
            scale: [1, 0.8, 1]
          }}
          transition={{ 
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-24 right-24 w-24 h-24 opacity-10"
          style={{
            background: `
              radial-gradient(circle at 20% 20%, rgba(255,255,255,0.08) 0%, transparent 60%),
              linear-gradient(135deg, rgba(55, 65, 81, 0.15) 0%, rgba(31, 41, 55, 0.15) 100%)
            `,
            borderRadius: '50%',
            filter: 'blur(1px)'
          }}
        />
      </div>

      {/* Fade edges - PC view only */}
      <div 
        className="hidden sm:block absolute top-0 left-0 right-0 z-20 pointer-events-none h-[100px]"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0) 100%)'
        }}
      />
      <div 
        className="hidden sm:block absolute bottom-0 left-0 right-0 z-20 pointer-events-none h-[100px]"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0) 100%)'
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Contact Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          {/* Contact Tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-4 sm:mb-6"
          >
            <PillBadge text={"Contact"} icon={''} className="mb-4 sm:mb-8" />
          </motion.div>

          {/* Main Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl tracking-tight mb-4 sm:mb-6"
          >
            <span className="text-[#999999]">{userData ? `Hi ${userData.name.split(' ')[0]}, let's get in ` : "Let's Get in "}</span>
            <span className="text-white">Touch</span>
          </motion.h2>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-[#999999] text-lg mb-4 sm:mb-8"
          >
            {userData ? `Hi ${userData.name}, let's connect and start with your project ASAP.` : "Let's connect and start with your project ASAP."}
          </motion.p>

          {/* Book a Call Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={openModal}
            className="bg-black border border-white/60 text-white px-6 py-3 rounded-full text-base font-medium hover:bg-white hover:text-black transition-all duration-300 flex items-center space-x-2 mx-auto mb-4 sm:mb-6"
          >
            <span>Book a call</span>
            <span className="relative inline-flex w-4 h-4 overflow-hidden">
              <motion.svg
                className="absolute inset-0 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                variants={{ hover: { x: [0, 8], opacity: [1, 0] } }}
                transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7h-6M17 7v6" />
              </motion.svg>
              <motion.svg
                className="absolute inset-0 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                initial={{ x: -8, opacity: 0 }}
                variants={{ hover: { x: [-8, 0, -8], opacity: [0, 1, 0] } }}
                transition={{ duration: 1.0, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7h-6M17 7v6" />
              </motion.svg>
            </span>
          </motion.button>

          {/* Email Link */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="text-[#999999]"
          >
            Or email{' '}
            <a 
              href="mailto:edit.prabhakar@gmail.com" 
              className="text-white hover:text-gray-300 transition-colors duration-300"
            >
              edit.prabhakar@gmail.com
            </a>
          </motion.p>
        </motion.div>
      </div>

      {/* Book Call Modal */}
      <BookCallModal isOpen={isModalOpen} onClose={closeModal} userData={userData} />
    </section>
  );
};

export default ContactSection;